import { createSlice } from '@reduxjs/toolkit';

import {
    loadDocComments,
    createComment,
    updateComment,
    deleteComment,
} from './doc-comments.middleware';
import { logout } from '../../auth/auth-middleware';
import { DocComment } from 'api';

type CommentsState = {
    list: DocComment[];
    error: string | null;
    isLoading: boolean;
};

type NewCommentState = {
    msg: string | null;
    error: string | null;
    isLoading: boolean;
};

type UpdatedCommentState = {
    msg: string | null;
    error: string | null;
    isLoading: boolean;
};

type deletedCommentState = {
    msg: string | null;
    error: string | null;
    isLoading: boolean;
};

type DocumentCommentsState = {
    comments: CommentsState;
    newComment: NewCommentState;
    updatedComment: UpdatedCommentState;
    deletedComment: deletedCommentState;
};

const initialState: DocumentCommentsState = {
    comments: { list: [], error: null, isLoading: false },
    newComment: { msg: null, error: null, isLoading: false },
    updatedComment: { msg: null, error: null, isLoading: false },
    deletedComment: { msg: null, error: null, isLoading: false },
};

export const docCommentsSlice = createSlice({
    name: 'app/document',
    initialState,
    reducers: {
        reset: (state) => {
            state.comments = { list: [], error: null, isLoading: false };
            state.newComment = { msg: null, error: null, isLoading: false };
            state.updatedComment = { msg: null, error: null, isLoading: false };
            state.deletedComment = { msg: null, error: null, isLoading: false };
        },
    },
    extraReducers: (builder) => {
        builder
            // GET
            .addCase(loadDocComments.fulfilled, (state, action) => {
                state.comments = {
                    list: action.payload.data.comments,
                    error: null,
                    isLoading: false,
                };
            })
            .addCase(loadDocComments.pending, (state) => {
                state.comments = {
                    list: [],
                    error: null,
                    isLoading: true,
                };
            })
            .addCase(loadDocComments.rejected, (state, action) => {
                const errorMsg = (action.payload as { msg: string }).msg
                    ? (action.payload as { msg: string }).msg
                    : 'Something went wrong';
                state.comments = {
                    list: [],
                    error: errorMsg,
                    isLoading: false,
                };
            })
            // POST
            .addCase(createComment.fulfilled, (state, action) => {
                state.newComment = {
                    msg: action.payload.data.msg,
                    error: null,
                    isLoading: false,
                };
            })
            .addCase(createComment.pending, (state) => {
                state.newComment = {
                    msg: null,
                    error: null,
                    isLoading: true,
                };
            })
            .addCase(createComment.rejected, (state, action) => {
                const errorMsg = (action.payload as { msg: string }).msg
                    ? (action.payload as { msg: string }).msg
                    : 'Something went wrong';
                state.newComment = {
                    msg: null,
                    error: errorMsg,
                    isLoading: false,
                };
            })
            // PUT
            .addCase(updateComment.fulfilled, (state, action) => {
                state.updatedComment = {
                    msg: action.payload.data.msg,
                    error: null,
                    isLoading: false,
                };
            })
            .addCase(updateComment.pending, (state) => {
                state.updatedComment = {
                    msg: null,
                    error: null,
                    isLoading: true,
                };
            })
            .addCase(updateComment.rejected, (state, action) => {
                const errorMsg = (action.payload as { msg: string }).msg
                    ? (action.payload as { msg: string }).msg
                    : 'Something went wrong';
                state.updatedComment = {
                    msg: null,
                    error: errorMsg,
                    isLoading: false,
                };
            })
            // DELETE
            .addCase(deleteComment.fulfilled, (state, action) => {
                state.deletedComment = {
                    msg: action.payload.data.msg,
                    error: null,
                    isLoading: false,
                };
            })
            .addCase(deleteComment.pending, (state) => {
                state.deletedComment = {
                    msg: null,
                    error: null,
                    isLoading: true,
                };
            })
            .addCase(deleteComment.rejected, (state, action) => {
                const errorMsg = (action.payload as { msg: string }).msg
                    ? (action.payload as { msg: string }).msg
                    : 'Something went wrong';
                state.deletedComment = {
                    msg: null,
                    error: errorMsg,
                    isLoading: false,
                };
            })
            .addCase(logout.fulfilled, (state) => {
                state.comments = { list: [], error: null, isLoading: false };
                state.newComment = { msg: null, error: null, isLoading: false };
                state.updatedComment = {
                    msg: null,
                    error: null,
                    isLoading: false,
                };
                state.deletedComment = { msg: null, error: null, isLoading: false };
            });
    },
});

export const { reset } = docCommentsSlice.actions;
export default docCommentsSlice.reducer;
